import { supabase } from '../supabase';
import { LudopediaTokenInfo, LudopediaUser } from '../../types';
import { generateRandomString } from '../../utils/helpers';

// Credenciais fornecidas pelo usuário
// Idealmente, essas credenciais viriam de variáveis de ambiente
const LUDOPEDIA_CREDENTIALS = {
  app_id: '9827225de7dd9aff',
  app_key: 'ef79f58d2e0cb1c11a2fb93aae0d9054',
};

// URLs da API da Ludopedia - Tentando diferentes variantes para resolver o problema da página em branco
const LUDOPEDIA_API_URL = '/api/ludopedia/api/v1';
// A URL de autorização com página em branco: https://ludopedia.com.br/oauth
// Vamos tentar uma abordagem mais direta e comum em implementações OAuth 2.0
const LUDOPEDIA_BASE_URL = 'https://ludopedia.com.br';
const LUDOPEDIA_TOKEN_URL = 'https://ludopedia.com.br/tokenrequest';

/**
 * Gera a URL de redirecionamento com base na origem da aplicação
 * @returns URL de redirecionamento para o callback da Ludopedia
 */
function getRedirectUri(): string {
  return window.location.origin + '/auth/ludopedia/callback';
}

/**
 * Gera a URL de autorização da Ludopedia
 * Testando diferentes variantes para resolver o problema da página em branco
 * @returns URL para redirecionar o usuário
 */
export function getLudopediaAuthUrl(): string {
  try {
    // Gera e armazena um estado para CSRF protection
    const state = generateRandomString(32);
    localStorage.setItem('ludopedia_oauth_state', state);
    
    // Parâmetros para a URL de autorização
    const urlParams = new URLSearchParams({
      app_id: LUDOPEDIA_CREDENTIALS.app_id,
      redirect_uri: getRedirectUri(),
      response_type: 'code',
      state: state,
      scope: 'collection'
    });
    
    console.log('[Ludopedia Auth] Gerando URL de autorização');
    console.log('[Ludopedia Auth] APP_ID:', LUDOPEDIA_CREDENTIALS.app_id);
    console.log('[Ludopedia Auth] REDIRECT_URI:', getRedirectUri());
    
    // Variante atual: Diretamente no caminho principal sem /authorize
    // Esta foi a versão mostrada na URL compartilhada pelo usuário, que resultou em página em branco
    const authUrl = `${LUDOPEDIA_BASE_URL}/oauth?${urlParams.toString()}`;
    
    // Verificação para debugging
    console.log('[Ludopedia Auth] URL gerada:', authUrl);
    console.log('[Ludopedia Auth] Esta URL está resultando em uma página em branco');
    console.log('[Ludopedia Auth] Tente adicionar "/autorize" na URL manualmente se esta não funcionar');
    
    // Gerar QR code para debugging via console - útil para testar em dispositivos móveis
    console.log(`[Ludopedia Auth] Link direto: ${authUrl}`);
    
    return authUrl;
  } catch (error) {
    console.error('[Ludopedia Auth] Erro ao gerar URL de autorização:', error);
    throw new Error('Erro ao gerar URL de autorização da Ludopedia');
  }
}

/**
 * Troca o código de autorização por um token de acesso
 * @param code Código de autorização recebido do provedor
 * @returns Informações do token de acesso
 */
export async function exchangeCodeForAccessToken(code: string): Promise<LudopediaTokenInfo> {
  try {
    console.log('[Ludopedia Auth] Trocando código por token de acesso...');
    
    const formData = new FormData();
    formData.append('app_id', LUDOPEDIA_CREDENTIALS.app_id);
    formData.append('app_key', LUDOPEDIA_CREDENTIALS.app_key);
    formData.append('code', code);
    formData.append('grant_type', 'authorization_code');
    formData.append('redirect_uri', getRedirectUri());
    
    console.log('[Ludopedia Auth] URL para obtenção do token:', LUDOPEDIA_TOKEN_URL);
    
    // URL para obtenção do token - Correta conforme a documentação
    const response = await fetch(LUDOPEDIA_TOKEN_URL, {
      method: 'POST',
      body: formData,
    });

    console.log('[Ludopedia Auth] Status da resposta:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Ludopedia Auth] Erro na resposta:', errorText);
      throw new Error(`Erro na resposta da API da Ludopedia: ${response.status} ${response.statusText}`);
    }
    
    // Obter texto da resposta para debug
    const responseText = await response.text();
    
    // Tentar analisar como JSON
    let responseData;
    try {
      responseData = JSON.parse(responseText);
    } catch (error) {
      console.error('[Ludopedia Auth] Erro ao analisar resposta JSON:', error);
      throw new Error('A resposta da API da Ludopedia não é um JSON válido');
    }
    
    // Verificar se houve erro na resposta
    if (responseData.error) {
      console.error('[Ludopedia Auth] Erro na resposta:', responseData);
      throw new Error(responseData.error_description || responseData.error || 'Erro desconhecido ao obter token');
    }
    
    // Verificar se o token de acesso está presente
    if (!responseData.access_token) {
      console.error('[Ludopedia Auth] Token de acesso ausente na resposta:', responseData);
      throw new Error('Token de acesso não encontrado na resposta');
    }
    
    // Converter para o formato esperado
    const tokenInfo: LudopediaTokenInfo = {
      accessToken: responseData.access_token,
      refreshToken: responseData.refresh_token || null,
      expiresIn: responseData.expires_in ? parseInt(responseData.expires_in) : 0,
      tokenType: responseData.token_type || 'Bearer',
    };
    
    console.log('[Ludopedia Auth] Token obtido com sucesso');
    return tokenInfo;
  } catch (error) {
    console.error('[Ludopedia Auth] Erro ao trocar código por token:', error);
    throw error; // Propagar erro para tratamento no componente
  }
}

/**
 * Registra um token de acesso fornecido manualmente pelo usuário
 * Função adicionada como alternativa ao fluxo OAuth que pode estar com problemas
 * @param userId ID do usuário
 * @param accessToken Token de acesso fornecido manualmente
 * @returns Resultado da operação
 */
export async function registerManualAccessToken(userId: string, accessToken: string): Promise<boolean> {
  try {
    // Verificar se o token é válido tentando buscar os dados do usuário
    const userData = await fetch(`${window.location.origin}/api/ludopedia/api/v1/usuario`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'APP-ID': LUDOPEDIA_CREDENTIALS.app_id,
        'APP-KEY': LUDOPEDIA_CREDENTIALS.app_key,
        'TOKEN': accessToken
      }
    });
    
    if (!userData.ok) {
      console.error('[Ludopedia Auth] Token manual inválido:', await userData.text());
      return false;
    }
    
    // Se chegou aqui, o token é válido
    const userDataJson = await userData.json();
    
    // Salvar o token no perfil
    return await saveUserToken(userId, accessToken, userDataJson);
  } catch (error) {
    console.error('[Ludopedia Auth] Erro ao registrar token manual:', error);
    return false;
  }
}

/**
 * Salva o token de acesso no perfil do usuário
 * @param userId ID do usuário
 * @param accessToken Token de acesso da Ludopedia
 * @param userData Dados do usuário da Ludopedia (opcional)
 */
export async function saveUserToken(userId: string, accessToken: string, userData?: LudopediaUser): Promise<boolean> {
  try {
    console.log('[Ludopedia Auth] Salvando token para usuário:', userId);
    
    // Dados básicos para atualização
    const updateData = {
      ludopedia_access_token: accessToken,
      ludopedia_connected_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    // Adicionar dados do usuário se disponíveis
    if (userData) {
      Object.assign(updateData, {
        ludopedia_user_id: userData.id,
        ludopedia_username: userData.username
      });
    }
    
    const { error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', userId);
    
    if (error) {
      console.error('[Ludopedia Auth] Erro ao salvar token:', error);
      return false;
    }
    
    console.log('[Ludopedia Auth] Token salvo com sucesso');
    return true;
  } catch (error) {
    console.error('[Ludopedia Auth] Erro ao salvar token:', error);
    return false;
  }
}

/**
 * Obtém o token de acesso do usuário
 * @param userId ID do usuário
 * @returns Token de acesso ou null se não existir
 */
export async function getUserToken(userId: string): Promise<string | null> {
  try {
    console.log('[Ludopedia Auth] Obtendo token para usuário:', userId);
    
    const { data, error } = await supabase
      .from('profiles')
      .select('ludopedia_access_token')
      .eq('id', userId)
      .single();
    
    if (error || !data) {
      console.error('[Ludopedia Auth] Erro ao obter token:', error);
      return null;
    }
    
    return data.ludopedia_access_token;
  } catch (error) {
    console.error('[Ludopedia Auth] Erro ao obter token:', error);
    return null;
  }
}

/**
 * Limpa o token de acesso do usuário
 * @param userId ID do usuário
 * @returns Verdadeiro se o token foi limpo com sucesso
 */
export async function clearUserToken(userId: string): Promise<boolean> {
  try {
    console.log('[Ludopedia Auth] Limpando token para usuário:', userId);
    
    const { error } = await supabase
      .from('profiles')
      .update({
        ludopedia_access_token: null,
        ludopedia_user_id: null,
        ludopedia_username: null,
        ludopedia_connected_at: null,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);
    
    if (error) {
      console.error('[Ludopedia Auth] Erro ao limpar token:', error);
      return false;
    }
    
    console.log('[Ludopedia Auth] Token limpo com sucesso');
    return true;
  } catch (error) {
    console.error('[Ludopedia Auth] Erro ao limpar token:', error);
    return false;
  }
}

/**
 * Verifica se o token do usuário é válido
 * @param userId ID do usuário
 * @returns Verdadeiro se o token for válido
 */
export async function isTokenValid(userId: string): Promise<boolean> {
  const token = await getUserToken(userId);
  
  if (!token) {
    return false;
  }
  
  // Testa o token fazendo uma chamada simples à API
  try {
    const response = await fetch(`${window.location.origin}/api/ludopedia/api/v1/usuario`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'APP-ID': LUDOPEDIA_CREDENTIALS.app_id,
        'APP-KEY': LUDOPEDIA_CREDENTIALS.app_key,
        'TOKEN': token
      }
    });
    
    if (!response.ok) {
      console.warn('[Ludopedia Auth] Token inválido ou expirado');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('[Ludopedia Auth] Erro ao verificar token:', error);
    return false;
  }
} 